import { useState } from "react";
import { useLocation, Link as WouterLink } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { UserRole } from "@/lib/types";
import { 
  Building2, 
  Menu, 
  X, 
  User, 
  LogOut, 
  Settings, 
  Home,
  Calendar,
  CreditCard,
  Building,
  Users,
  LogIn
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { HeaderInstallButton } from "@/components/ui/header-install-button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import logo from '@/assets/staymiddle-logo.png';
import logoWhite from '@/assets/staymiddle-logo-white.png';
import { Badge } from "@/components/ui/badge";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logoutMutation, isLoading } = useAuth();
  const [location] = useLocation();
  
  // Debug user object
  console.log("User object in Header:", user);

  // Function to get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.fullName) return "U";
    return user.fullName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Handle logout
  const handleLogout = () => {
    logoutMutation.mutate();
  };

  // Get user dashboard link based on role
  const getDashboardLink = () => {
    if (!user) return "/auth";
    
    // All authenticated users use the unified dashboard
    if (user.role === "owner") {
      return "/owner/dashboard";
    } else if (user.role === "manager") {
      return "/manager/dashboard";
    } else if (user.role === "agent") {
      return "/agent/dashboard";
    } else if (user.role === "admin") {
      return "/properties";
    } else if (user.role === "guest") {
      return "/dashboard";
    }
    
    // Default fallback
    return "/dashboard";
  };
 

  // User dropdown items: only username/email and logout
  const getUserDropdownItems = () => {
    if (!user) return null;
    return (
      <>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </>
    );
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo and hamburger menu for mobile: always visible on all pages */}
        <div className="flex items-center gap-2 md:hidden">
          {!isLoading && !user ? (
            // Mobile menu for non-logged-in users
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  className="p-2 mr-2 rounded hover:bg-accent"
                  title="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 bg-primary">
                <div className="flex flex-col h-full text-white">
                  {/* Header */}
                  <div className="p-6 border-b border-white/20">
                    <WouterLink href="/" onClick={() => setMobileMenuOpen(false)}>
                      <div className="cursor-pointer flex items-center gap-2">
                        <img src={logoWhite} alt="StayMiddle Logo" className="h-9 w-auto" />
                      </div>
                    </WouterLink>
                  </div>
                  
                  {/* Navigation */}
                  <div className="flex-1 p-6 space-y-4">
                    <div
                      onClick={() => {
                        window.location.href = "/";
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        location === "/" ? "bg-accent text-accent-foreground" : "hover:bg-white/10 text-white"
                      }`}
                    >
                      <Home className="h-5 w-5" />
                      <span className="font-medium">Home</span>
                    </div>
                    
                    <div
                      onClick={() => {
                        window.location.href = "/properties";
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        location.startsWith("/properties") ? "bg-accent text-accent-foreground" : "hover:bg-white/10 text-white"
                      }`}
                    >
                      <Building className="h-5 w-5" />
                      <span className="font-medium">Browse Properties</span>
                    </div>
                    
                    <div
                      onClick={() => {
                        window.location.href = "/how-it-works";
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        location === "/how-it-works" ? "bg-accent text-accent-foreground" : "hover:bg-white/10 text-white"
                      }`}
                    >
                      <User className="h-5 w-5" />
                      <span className="font-medium">How It Works</span>
                    </div>
                  </div>
                  
                  {/* Auth buttons */}
                  <div className="p-6 border-t border-white/20">
                    <WouterLink href="/auth?tab=login">
                      <Button 
                        variant="outline" 
                        className="w-full border-white bg-white text-primary hover:bg-gray-100 hover:text-primary font-medium mb-4"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <LogIn className="mr-2 h-4 w-4" />
                        Sign In
                      </Button>
                    </WouterLink>
                    <WouterLink href="/auth?tab=register">
                      <Button 
                        className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Create Account
                      </Button>
                    </WouterLink>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          ) : (
            // Hamburger menu for logged-in users (existing functionality)
            <button
              className="p-2 mr-2 rounded hover:bg-accent"
              onClick={() => window.dispatchEvent(new CustomEvent('open-sidebar'))}
              title="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          )}
          <WouterLink href="/">
            <div className="cursor-pointer flex items-center gap-2">
              <img src={logo} alt="StayMiddle Logo" className="h-9 w-auto" />
            </div>
          </WouterLink>
        </div>
        {/* Desktop logo: only show if sidebar is NOT present (not on /manager, /owner, /agent, /admin routes) */}
        <div className="hidden md:flex">
        {!location.startsWith("/dashboard") && !location.startsWith("/manager") && !location.startsWith("/owner") && !location.startsWith("/agent") &&
           !location.startsWith("/admin") && !location.startsWith("/settings") && !location.startsWith("/bookings") && !location.startsWith("/stripe-connect") && (
             <WouterLink href="/">
              <div className="cursor-pointer flex items-center gap-2">
                <img src={logo} alt="StayMiddle Logo" className="h-9 w-auto" />
              </div>
            </WouterLink>
          )}
        </div>
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          {!isLoading && !user ? (
            // When not logged in, show regular navigation
            <>
              <div 
                onClick={() => window.location.href = "/"} 
                className={`text-sm font-medium transition-colors hover:text-primary cursor-pointer ${location === "/" ? "text-primary" : "text-foreground/60"}`}
              >
                Home
              </div>
              <div 
                onClick={() => window.location.href = "/properties"} 
                className={`text-sm font-medium transition-colors hover:text-primary cursor-pointer ${location.startsWith("/properties") ? "text-primary" : "text-foreground/60"}`}
              >
                Properties
              </div>
              <div 
                onClick={() => window.location.href = "/how-it-works"} 
                className={`text-sm font-medium transition-colors hover:text-primary cursor-pointer ${location === "/how-it-works" ? "text-primary" : "text-foreground/60"}`}
              >
                How It Works
              </div>
            </>
          ) : null}
        </nav>

        {/* User section / Auth buttons */}
        <div className="flex items-center gap-4">
          <HeaderInstallButton />
          {!isLoading && user && !location.startsWith("/dashboard") && !location.startsWith("/manager") && !location.startsWith("/owner") && !location.startsWith("/agent") && !location.startsWith("/admin")  && 
          !location.startsWith("/settings") &&  !location.startsWith("/stripe-connect") && 
          (
            <div
              onClick={() => window.location.href = getDashboardLink()}
              className={`mr-3 px-4 py-2 rounded-md cursor-pointer font-semibold transition-colors
                ${location === getDashboardLink()
                  ? "bg-primary text-primary-foreground shadow"
                  : "bg-muted text-foreground/80 hover:bg-accent"}
              `}
            >
              Dashboard
            </div>
          )}
          {!isLoading && (
            <>
              {user ? (
                <div className="flex items-center gap-2">
                  {/* Show pending/inactive badge for manager/agent */}
                  {(user.role === "manager" || user.role === "agent") && (user.status === "pending" || user.status === "inactive") && (
                    <Badge className="bg-yellow-400 text-yellow-900 px-2 py-1 text-xs font-semibold mr-1">
                      {user.status === "pending" ? "Pending Approval" : "Inactive"}
                    </Badge>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                        <Avatar className="h-10 w-10 border">
                          <AvatarFallback>{getUserInitials()}</AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="flex items-center justify-start gap-2 p-2">
                        <div className="flex flex-col space-y-0.5">
                          <p className="text-sm font-medium">{user.fullName}</p>
                          <p className="text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                      </div>
                      {getUserDropdownItems()}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <div className="hidden md:flex items-center gap-2">
                  <WouterLink href="/auth?tab=login">
                    <Button variant="ghost">
                      Sign In
                    </Button>
                  </WouterLink>
                  <WouterLink href="/auth?tab=register">
                    <Button>
                      Create Account
                    </Button>
                  </WouterLink>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}